/**
 * Icons, in the reference's line style: 24×24, 1.7 stroke, round joins.
 * All decorative — meaning is always carried by adjacent text.
 */

import type { ReactNode } from 'react';

function Svg({ children, size = 17 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

type IconProps = { size?: number };

export function IconTenants({ size }: IconProps) {
  return (
    <Svg {...(size === undefined ? {} : { size })}>
      <path d="M4 20V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v14" />
      <path d="M14 20V11h4a2 2 0 0 1 2 2v7" />
      <path d="M3 20h18M7.5 8h3M7.5 12h3M7.5 16h3" />
    </Svg>
  );
}

export function IconCatalog({ size }: IconProps) {
  return (
    <Svg {...(size === undefined ? {} : { size })}>
      <path d="M9 6h11M9 12h11M9 18h11" />
      <circle cx="4.5" cy="6" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="4.5" cy="12" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="4.5" cy="18" r="1.3" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Nodes: two machines and the line between them. */
export function IconNodes({ size }: IconProps) {
  return (
    <Svg {...(size === undefined ? {} : { size })}>
      <rect x="3" y="4" width="18" height="6" rx="1.5" />
      <rect x="3" y="14" width="18" height="6" rx="1.5" />
      <path d="M7 10v4" />
      <circle cx="6.5" cy="7" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="6.5" cy="17" r="0.9" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Audit: a record with a line of entries, and a tick against the last. */
export function IconAudit({ size }: IconProps) {
  return (
    <Svg {...(size === undefined ? {} : { size })}>
      <path d="M5 3h9l5 5v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M14 3v5h5" />
      <path d="M8 12h6M8 16h3" />
      <path d="m14.5 17 1.5 1.5 3-3" />
    </Svg>
  );
}

export function IconChevronRight({ size }: IconProps) {
  return (
    <Svg {...(size === undefined ? {} : { size })}>
      <path d="m9 6 6 6-6 6" />
    </Svg>
  );
}

export function IconChevronLeft({ size }: IconProps) {
  return (
    <Svg {...(size === undefined ? {} : { size })}>
      <path d="m15 6-6 6 6 6" />
    </Svg>
  );
}

export function IconPlus({ size }: IconProps) {
  return (
    <Svg {...(size === undefined ? {} : { size })}>
      <path d="M12 5v14M5 12h14" />
    </Svg>
  );
}

export function IconClose({ size }: IconProps) {
  return (
    <Svg {...(size === undefined ? {} : { size })}>
      <path d="m6 6 12 12M18 6 6 18" />
    </Svg>
  );
}

export function IconSun({ size }: IconProps) {
  return (
    <Svg {...(size === undefined ? {} : { size })}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </Svg>
  );
}

export function IconMoon({ size }: IconProps) {
  return (
    <Svg {...(size === undefined ? {} : { size })}>
      <path d="M20 14.5A8.5 8.5 0 1 1 9.5 4a6.6 6.6 0 0 0 10.5 10.5z" />
    </Svg>
  );
}

export function IconSignOut({ size }: IconProps) {
  return (
    <Svg {...(size === undefined ? {} : { size })}>
      <path d="M15 17l5-5-5-5M20 12H9M12 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h6" />
    </Svg>
  );
}

export function IconSearch({ size }: IconProps) {
  return (
    <Svg {...(size === undefined ? {} : { size })}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4 4" />
    </Svg>
  );
}

export function IconTrash({ size }: IconProps) {
  return (
    <Svg {...(size === undefined ? {} : { size })}>
      <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
      <path d="M6.5 7l.9 12.1a1 1 0 0 0 1 .9h7.2a1 1 0 0 0 1-.9L17.5 7" />
    </Svg>
  );
}

export function IconIdentity({ size }: IconProps) {
  return (
    <Svg {...(size === undefined ? {} : { size })}>
      <circle cx="9" cy="9.5" r="3.2" />
      <path d="M3.8 19.5a5.6 5.6 0 0 1 10.4 0" />
      <path d="M16 6.5h4.5M16 10h4.5M17.5 13.5h3" />
    </Svg>
  );
}

export function IconVersions({ size }: IconProps) {
  return (
    <Svg {...(size === undefined ? {} : { size })}>
      <path d="M12 3v11" />
      <path d="m8 7 4-4 4 4" />
      <path d="M4 15c1.6-1.4 3.1-1.4 4.7 0s3.1 1.4 4.7 0 3.1-1.4 4.7 0" />
      <path d="M4 20c1.6-1.4 3.1-1.4 4.7 0s3.1 1.4 4.7 0 3.1-1.4 4.7 0" />
    </Svg>
  );
}

export function IconKey({ size }: IconProps) {
  return (
    <Svg {...(size === undefined ? {} : { size })}>
      <circle cx="8" cy="15" r="3.5" />
      <path d="m10.5 12.5 8-8M16 7l2.5 2.5M13.5 9.5 16 12" />
    </Svg>
  );
}

export function IconExternal({ size }: IconProps) {
  return (
    <Svg {...(size === undefined ? {} : { size })}>
      <path d="M14 4h6v6M20 4l-8.5 8.5" />
      <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
    </Svg>
  );
}

export function IconLanguage({ size }: IconProps) {
  return (
    <Svg {...(size === undefined ? {} : { size })}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c2.2 2.3 3.4 5.3 3.4 8.5S14.2 18.2 12 20.5c-2.2-2.3-3.4-5.3-3.4-8.5S9.8 5.8 12 3.5z" />
    </Svg>
  );
}

/**
 * A claim: something the thing itself said about itself, which nobody checked.
 *
 * A quotation mark rather than a warning sign — this is not a fault, and a
 * table full of warnings about ordinary rows teaches people to ignore warnings.
 */
export function IconClaimed({ size = 15 }: { size?: number }) {
  return (
    <Svg size={size}>
      <path d="M9.5 7.5c-1.6 0-2.5 1-2.5 2.3 0 1.2.9 2.2 2.2 2.2.4 0 .8-.1 1-.2-.2 1.6-1.2 2.7-2.6 3.2" />
      <path d="M16.5 7.5c-1.6 0-2.5 1-2.5 2.3 0 1.2.9 2.2 2.2 2.2.4 0 .8-.1 1-.2-.2 1.6-1.2 2.7-2.6 3.2" />
    </Svg>
  );
}

/* ------------------------------------------------------------------
   One icon per kind of resource. The reference build's own drawings:
   a terminal for a shell, a cylinder for a database, a hexagon for a
   cluster, a spark for a model, a star for a tool server, a signal for
   a raw port.

   A kind neither side knows gets the neutral one rather than the
   nearest-looking — a wrong picture is read faster than a wrong word,
   and believed longer.
   ------------------------------------------------------------------ */

export function IconSsh({ size = 16 }: { size?: number }) {
  return (
    <Svg size={size}>
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <path d="M7 9l3 3-3 3M13 15h4" />
    </Svg>
  );
}

export function IconDatabase({ size = 16 }: { size?: number }) {
  return (
    <Svg size={size}>
      <ellipse cx="12" cy="6" rx="7" ry="3" />
      <path d="M5 6v12c0 1.66 3.13 3 7 3s7-1.34 7-3V6" />
      <path d="M5 12c0 1.66 3.13 3 7 3s7-1.34 7-3" />
    </Svg>
  );
}

export function IconKube({ size = 16 }: { size?: number }) {
  return (
    <Svg size={size}>
      <path d="M12 2.5l8 4v7l-8 4-8-4v-7l8-4z" />
      <path d="M12 8.5l4 2v3l-4 2-4-2v-3l4-2z" />
    </Svg>
  );
}

export function IconTool({ size = 16 }: { size?: number }) {
  return (
    <Svg size={size}>
      <path d="M12 3l2.2 4.6L19 8.3l-3.5 3.4.8 4.9L12 14.3 7.7 16.6l.8-4.9L5 8.3l4.8-.7L12 3z" />
    </Svg>
  );
}

export function IconPort({ size = 16 }: { size?: number }) {
  return (
    <Svg size={size}>
      <path d="M4 12h4l2-5 4 10 2-5h4" />
    </Svg>
  );
}

export function IconModel({ size = 16 }: { size?: number }) {
  return (
    <Svg size={size}>
      <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" />
    </Svg>
  );
}

/** An HTTP application: a browser window with something laid out in it. */
export function IconWeb({ size = 16 }: { size?: number }) {
  return (
    <Svg size={size}>
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <path d="M3 9h18M6.5 6.5h.01M9 6.5h.01" />
    </Svg>
  );
}

/** The way out: an arrow leaving an enclosure, which is what the lane is. */
export function IconEgress({ size = 16 }: { size?: number }) {
  return (
    <Svg size={size}>
      <path d="M13 4H6a2 2 0 00-2 2v12a2 2 0 002 2h7" />
      <path d="M16 8l4 4-4 4M10 12h10" />
    </Svg>
  );
}

export function IconResource({ size = 16 }: { size?: number }) {
  return (
    <Svg size={size}>
      <rect x="4" y="4" width="16" height="16" rx="3" />
    </Svg>
  );
}

export function Logo() {
  return (
    <span className="logomark">
      <svg viewBox="0 0 32 32" fill="none" width="30" height="30" aria-hidden="true" focusable="false">
        <rect x="3" y="3" width="26" height="26" rx="7" stroke="currentColor" strokeWidth="1.8" opacity="0.22" />
        <path d="M11 9V23" stroke="var(--accent)" strokeWidth="2.4" strokeLinecap="round" />
        <path d="M16 9V23" stroke="var(--accent)" strokeWidth="2.4" strokeLinecap="round" opacity="0.6" />
        <path d="M21 9V23" stroke="var(--accent)" strokeWidth="2.4" strokeLinecap="round" opacity="0.32" />
      </svg>
    </span>
  );
}
